import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import URL from 'src/api/url'
import { withStyles } from '@material-ui/core'
import styles from './styles'
import { LandingPagePreview, TemplateSelect, Outline } from './components'
import { useParams } from 'react-router-dom'
import {
  getCollection,
  getCollectionLandingPageAction,
} from 'src/reducers/collection/action'
import { getCourseLandingPageAction } from 'src/reducers/course/action'
import Header from './components/header'
import TemplateRenderer from './data/template-renderer'
import { useCourseSettings } from 'src/hooks/useCourseSettings'
import HeaderTheme from './components/header-theme'
// Import updated LandingPageHeader
import { LandingPageHeader } from './components'
import { Helmet } from 'react-helmet'
import PortalFrame from './components/portal-frame'
import { LINK_CONTEXTS } from 'src/utils/portal-links'
import {
  useWorkspaceBranding,
  withWorkspaceStyle,
} from 'src/utils/workspace-logo'

// Wait at most this long for the landing page before drawing the preview.
const PREVIEW_TIMEOUT = 5000

const LandingPagePreviewMain = props => {
  // `notice`: shown above the landing page card (e.g. the course's draft note).
  const { classes, notice } = props
  const course = useSelector(state => state.course.course)
  const pageUrl = window.location.pathname.split('/')[1]
  const [isLoading, setIsLoading] = useState(false)
  const [customHtmlContent, setCustomHtmlContent] = useState('')
  const [selectedTemplateId, setSelectedTemplateId] = useState(0)
  const [showTemplateSelect, setShowTemplateSelect] = useState(true) // State to control template visibility
  const [previewFont, setPreviewFont] = useState(null) // New state for font preview
  // The preset being previewed (not applied yet); the header preview renders it.
  const [previewTemplate, setPreviewTemplate] = useState(null)
  // The id whose landing page has loaded. Until then the store may still hold
  // the previously opened page's, whose header would show and then resize.
  const [loadedFor, setLoadedFor] = useState(null)
  const workspaceBranding = useWorkspaceBranding()
  const { id, page, subPage } = useParams()
  const dispatch = useDispatch()
  const courseSettings = useSelector(state => state.course.landingPageSettings)
  const collectionSettings = useSelector(
    state => state.collection.landingPageSettings,
  )
  const collection = useSelector(state => state.collection.collection)
  const isShowcase = pageUrl === 'collection' && collection?.type === 1
  // "In portal" vs "Direct link": drives the header URL and the preview frame.
  const [linkContext, setLinkContext] = useState(LINK_CONTEXTS.PORTAL)
  const { value } = useCourseSettings('theme')
  // Portal breadcrumb after Home: the path, then the course (when in one).
  const coursePath =
    course?.courseCollectionId &&
    String(collection?.id) === String(course.courseCollectionId)
      ? collection
      : null
  const breadcrumbs =
    pageUrl === 'mini-course'
      ? [coursePath?.name, course?.name]
      : [collection?.name]
  // Then conditionally use the results
  const landingPageData =
    pageUrl === 'mini-course' ? courseSettings : collectionSettings

  const showLayoutComponents =
    pageUrl === 'collection'
      ? true
      : (value === null ? 0 : value?.layout) === 0
      ? true
      : false
  useEffect(() => {
    let active = true
    const loaded = () => active && setLoadedFor(id)
    // The request's callback only runs on success; stop waiting after this.
    const timer = setTimeout(loaded, PREVIEW_TIMEOUT)
    if (pageUrl === 'collection') {
      dispatch(getCollection(id))
      dispatch(getCollectionLandingPageAction(id, loaded))
    } else if (pageUrl === 'mini-course') {
      dispatch(getCourseLandingPageAction(id, loaded))
    }
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [id])
  const previewReady = loadedFor === id

  // Set the initial HTML content when landing page data is loaded
  useEffect(() => {
    if (landingPageData && landingPageData.html) {
      setCustomHtmlContent(landingPageData.html)
    }

    // Reset preview font when landingPageData changes
    setPreviewFont(null)
  }, [landingPageData])

  const themeSettingsStringified = course?.settingDtos?.find(
    setting => setting.key === 'theme',
  )
  const themeSettings = themeSettingsStringified
    ? JSON.parse(themeSettingsStringified?.value)
    : null

  const fontSize = themeSettings?.fontSize
  // Use previewFont if available, otherwise use the font from landingPageData
  // A page left on the default font shows the workspace branding's (courses
  // too).
  const font =
    previewFont ||
    withWorkspaceStyle(landingPageData, workspaceBranding)?.font ||
    landingPageData?.font

  const handleSetHtmlContent = html => {
    // Force a proper state change by creating a new string
    const newHtml = String(html || '')
    setCustomHtmlContent(newHtml)
  }

  // New function to handle template selection
  const handleTemplateSelection = templateId => {
    setSelectedTemplateId(templateId)
  }

  // New function to handle font selection for preview
  const handleFontSelection = font => {
    // Update the local state with the selected font
    setPreviewFont(font)
  }

  // New function to toggle template selector visibility
  const toggleTemplateSelect = () => {
    setShowTemplateSelect(prev => !prev)
  }

  return (
    <>
      <Helmet>
        <link
          rel="stylesheet"
          href={`https://fonts.googleapis.com/css?family=${font}:wght@100;200;300;400;500;600;700;800;900&display=swap`}
        />
      </Helmet>
      <div
        className={
          pageUrl === 'mini-course' ? classes.root : classes.collectionRoot
        }
      >
        <div className={classes.wrapper}>
          <TemplateSelect
            pageUrl={pageUrl}
            setHtmlContent={handleSetHtmlContent}
            setSelectedTemplateId={handleTemplateSelection}
            setSelectedFont={handleFontSelection}
            setPreviewTemplate={setPreviewTemplate}
          />
          {notice}
          <div className={classes.card}>
            {/* Pass the selectedTemplateId to LandingPageHeader */}
            <LandingPageHeader
              pageUrl={pageUrl}
              course={course}
              selectedTemplateId={selectedTemplateId}
              linkContext={linkContext}
              onLinkContextChange={setLinkContext}
            />
            <PortalFrame
              // The portal's own pages always show its header and footer.
              inPortal={isShowcase || linkContext === LINK_CONTEXTS.PORTAL}
              entityLabel={pageUrl === 'mini-course' ? 'course' : 'path'}
              showNote={!isShowcase}
              breadcrumbs={isShowcase ? null : breadcrumbs}
            >
              {!previewReady && <div className={classes.previewPlaceholder} />}
              {previewReady && showLayoutComponents === true && (
                <Header
                  pageUrl={pageUrl}
                  previewTemplate={previewTemplate}
                  inPortal={isShowcase || linkContext === LINK_CONTEXTS.PORTAL}
                />
              )}
              {previewReady && showLayoutComponents === false && (
                <HeaderTheme pageUrl={pageUrl} />
              )}

              {!previewReady ? null : landingPageData &&
              landingPageData.metadata !== null &&
              landingPageData.initialized !== true ? (
                <TemplateRenderer
                  selectedTemplate={selectedTemplateId}
                  fontFamily={font}
                  fontSize={fontSize}
                  theme={value === null ? 0 : value?.layout}
                  courseData={landingPageData && landingPageData.metadata}
                />
              ) : (
                <LandingPagePreview
                  key={customHtmlContent ? 'custom' : 'default'}
                  pageUrl={pageUrl}
                  fontFamily={font}
                  fontSize={fontSize}
                  theme={value === null ? 0 : value?.layout}
                  customHtmlContent={customHtmlContent}
                />
              )}
            </PortalFrame>
          </div>
        </div>
      </div>
    </>
  )
}

export default withStyles(styles)(LandingPagePreviewMain)
